import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface TextImageSectionProps {
  content: {
    title?: string;
    content?: string;
    imageUrl?: string;
    imagePosition?: 'left' | 'right';
    ctaText?: string;
    ctaLink?: string;
  };
  variant?: string;
  layoutStyle?: string;
}

export function TextImageSection({ content, variant = 'default', layoutStyle = 'classic' }: TextImageSectionProps) {
  const {
    title,
    content: textContent,
    imageUrl,
    imagePosition = 'right',
    ctaText,
    ctaLink,
  } = content;

  const isMinimal = layoutStyle === 'modern-minimal';
  const isSerif = layoutStyle === 'elegant-serif';
  const isBold = layoutStyle === 'bold-geometric';

  return (
    <section className={cn(
      "section-padding",
      variant === 'alt-bg' ? "bg-secondary/30" : "bg-background"
    )}>
      <div className="container mx-auto px-4">
        <div className={cn(
          "grid md:grid-cols-2 gap-8 md:gap-16 items-center",
          imagePosition === 'left' && "md:[direction:rtl]"
        )}>
          <div className={cn(
            "space-y-6",
            imagePosition === 'left' && "md:[direction:ltr]"
          )}>
            {title && (
              <h2 className={cn(
                "font-display font-bold text-foreground",
                isMinimal ? "text-3xl md:text-4xl tracking-tight" :
                isSerif ? "text-3xl md:text-4xl italic" :
                isBold ? "text-3xl md:text-5xl uppercase tracking-wide" :
                "text-3xl md:text-4xl"
              )}>
                {title}
              </h2>
            )}
            {textContent && (
              <div className={cn(
                "text-muted-foreground leading-relaxed",
                isSerif ? "font-serif text-lg" : "text-lg"
              )}>
                {textContent.split('\n').map((paragraph, i) => (
                  <p key={i} className="mb-4">{paragraph}</p>
                ))}
              </div>
            )}
            {ctaText && ctaLink && (
              <Button variant="hero" className="group" asChild>
                <Link to={ctaLink}>
                  {ctaText}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            )}
          </div>

          <div className={cn(imagePosition === 'left' && "md:[direction:ltr]")}>
            {imageUrl ? (
              <img 
                src={imageUrl} 
                alt="" 
                className={cn(
                  "w-full aspect-[4/3] object-cover shadow-lg",
                  isMinimal ? "rounded-none" : 
                  isBold ? "rounded-none" : 
                  "rounded-2xl"
                )}
              />
            ) : (
              <div className="aspect-[4/3] bg-muted rounded-2xl flex items-center justify-center">
                <p className="text-muted-foreground">No image set</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}